import { copyURLToClipboard, dataLayerPush } from './helpers';

const { apiFetch } = wp;
const { addQueryArgs } = wp.url;
const { _nx, __ } = wp.i18n;
const { round } = Math;

/**
 * Gets the answer meta for this quiz.
 * @param {int} challengeId Post ID of the quiz.
 * @returns {array}
 */
const getAnswersMeta = async (challengeId) => {
	const res = await apiFetch({
		path: addQueryArgs('/mavis/v1/challenge-answers', {
			post_id: challengeId,
		}),
	});
	return res;
};

/**
 * Helper function to hide an element from view & a screenreader.
 * @param {Element} el Element in DOM.
 */
const hideElement = (el, isFocusable = false) => {
	el.classList.add('is-hidden');
	if (isFocusable) {
		el.setAttribute('disabled', '');
	} else {
		el.setAttribute('aria-hidden', 'true');
	}
};

/**
 * Helper function to re-show an element.
 * @param {Element} el Element in DOM.
 */
const showElement = (el, isFocusable = false) => {
	el.classList.remove('is-hidden');
	if (isFocusable) {
		el.removeAttribute('disabled');
	} else {
		el.removeAttribute('aria-hidden');
	}
};

/**
 * Helper function to transform an array of strings to lower case.
 * @param {array} arr Array to convert.
 * @returns {array}
 */
const arrayToLowerCase = (arr) => arr.map((item) => item.toLowerCase());

window.addEventListener('DOMContentLoaded', async () => {
	const challengeBlock = document.querySelector('.wp-block-mavis-challenge');
	if (!challengeBlock) {
		return;
	}

	/** Variable setup */
	const { challengeId, timerMinutes, timerSeconds } = challengeBlock.dataset;
	const answersMeta = await getAnswersMeta(parseInt(challengeId, 10));
	let timerInterval;
	let correctSubmissions = [];
	let shouldUpdateTotalParticipants = true;
	let shouldStartTimer = true;
	let fullDurationInSeconds = parseInt(timerMinutes * 60, 10) + parseInt(timerSeconds, 10);
	let correctAnswers = [];

	/** Constants */
	const formEl = challengeBlock.querySelector('form');
	const inputEl = challengeBlock.querySelector('input');

	// Playing area elements
	const buttonsWrapper = challengeBlock.querySelector('.mavisChallenge_playingArea-buttonsWrapper');
	const answersOutputEl = challengeBlock.querySelector('.mavisChallenge_playingArea-answersOutput');
	const currentScoreEl = challengeBlock.querySelector('.mavisChallenge_playingArea-currentScore');
	const answerCountEl = challengeBlock.querySelector('.mavisChallenge_playingArea-answerCount');
	const timerTextEl = challengeBlock.querySelector('.mavisChallenge_playingArea-timerText');
	const answersOutputWrapperEl = challengeBlock.querySelector(
		'.mavisChallenge_playingArea-answerOutputWrapper',
	);
	const initTextEl = challengeBlock.querySelector('.mavisChallenge_playingArea-initText');
	const giveUpBtn = challengeBlock.querySelector('.wp-block-button.is-give-up');
	const stopTimerBtn = challengeBlock.querySelector('.wp-block-button.is-stop-timer');
	const seeResultsBtn = challengeBlock.querySelector('.wp-block-button.is-see-results');
	const incorrectAnswerText = challengeBlock.querySelector(
		'.mavisChallenge_playingArea-incorrectAnswerText',
	);
	const submitBtnWrapper = challengeBlock.querySelector('.mavisChallenge_playingArea-submit');
	const submitBtn = challengeBlock.querySelector('.mavisChallenge_playingArea-submit button');
	const howAreYouDoingText = challengeBlock.querySelector(
		'.mavisChallenge_playingArea-howAreYouDoingText',
	);

	// Results elements
	const resultsEl = challengeBlock.querySelector('.mavisChallenge_results');
	const resultsHeaderEl = challengeBlock.querySelector('.mavisChallenge_results-header');
	const profileTextEl = challengeBlock.querySelector('.mavisChallenge_results-profileText');
	const answersListEl = challengeBlock.querySelector('.mavisChallenge_results-answersList');
	const shareBtn = challengeBlock.querySelector('#share');
	const playAgainBtn = challengeBlock.querySelector('#play-again');
	const resultsAnswersDisplayEl = challengeBlock.querySelector(
		'.mavisChallenge_results-answersDisplay',
	);

	/**
	 * Get the total amount of seconds elapsed.
	 * @returns {integer}
	 */
	const getSecondsElapsed = () => {
		const startingDurationInSeconds = parseInt(timerMinutes * 60, 10) + parseInt(timerSeconds, 10);
		return startingDurationInSeconds - fullDurationInSeconds;
	};

	/**
	 * Hides the input playing area so the user can no longer enter answers.
	 */
	const hidePlayingArea = () => {
		inputEl.setAttribute('disabled', true);
		hideElement(inputEl, true);
		hideElement(giveUpBtn, true);
		hideElement(stopTimerBtn, true);
		hideElement(incorrectAnswerText, false);
		hideElement(submitBtnWrapper, false);
		hideElement(answerCountEl, false);
		hideElement(howAreYouDoingText, false);
	};

	/**
	 * Sets up the correct answers array ready for play.
	 */
	const populateAnswersArray = () => {
		correctAnswers = [];
		answersMeta.forEach((answerRecord) => {
			correctAnswers.push({ id: answerRecord.id, text: answerRecord.text });
			answerRecord.synonyms.forEach(
				(synonym) =>
					correctAnswers.push({
						id: answerRecord.id,
						text: synonym,
						parentText: answerRecord.text,
					}), // eslint-disable-line no-return-assign
			);
		});
	};

	/**
	 * Handles the user clicking play again.
	 * @param {Event} event - Button click event.
	 */
	const handlePlayAgain = (event) => {
		event.preventDefault();

		// Reset all of our variables
		shouldUpdateTotalParticipants = true;
		shouldStartTimer = true;
		correctSubmissions = [];
		fullDurationInSeconds = parseInt(timerMinutes * 60, 10) + parseInt(timerSeconds, 10);
		populateAnswersArray();

		// Reset playing area
		showElement(initTextEl);
		showElement(formEl);
		showElement(inputEl, true);
		inputEl.value = '';
		inputEl.removeAttribute('disabled');
		inputEl.classList.remove('is-incorrect');
		answersOutputEl.textContent = '';
		currentScoreEl.textContent = '0';
		timerTextEl.textContent = '';
		incorrectAnswerText.textContent = '';

		// Reset results.
		profileTextEl.textContent = '';
		const profileImage = resultsHeaderEl.querySelector('img');
		if (profileImage) {
			profileImage.remove();
		}
		answersListEl.textContent = '';
		resultsEl.classList.remove('is-bad-result', 'is-in-between-result', 'is-good-result');

		// Show/hide relevant elements.
		hideElement(timerTextEl);
		hideElement(buttonsWrapper);
		hideElement(answersOutputWrapperEl);
		hideElement(resultsEl);
		hideElement(answerCountEl, true);
		hideElement(seeResultsBtn, true);
		hideElement(resultsAnswersDisplayEl);
		hideElement(incorrectAnswerText, false);
		hideElement(howAreYouDoingText, false);
		showElement(stopTimerBtn, true);
		showElement(giveUpBtn, true);
		showElement(submitBtnWrapper, false);
	};

	/**
	 * Submits the quiz completion to post meta which returns the result profile.
	 * @returns {object}
	 */
	const submitScore = async () => {
		const res = await apiFetch({
			path: `/mavis/v1/challenge-submit-score`,
			method: 'POST',
			data: {
				post_id: challengeId,
				score: correctSubmissions.length,
			},
		});
		return JSON.parse(res);
	};

	/**
	 * Hides the playing area for the quiz and shows the results.
	 */
	const showResults = async (event = null) => {
		if (event) {
			event.preventDefault();
		}

		clearInterval(timerInterval);
		const { image, text } = await submitScore();
		const scorePrefixEl = challengeBlock.querySelector('.mavisChallenge_results-scorePrefix');
		scorePrefixEl.textContent = `${correctSubmissions.length}`;
		profileTextEl.textContent = text;
		resultsHeaderEl.insertAdjacentHTML('beforeend', image);
		answersListEl.textContent = correctSubmissions.join(', ');
		hideElement(formEl);

		if (correctSubmissions.length > 0) {
			showElement(resultsAnswersDisplayEl);
		}

		const scorePercentage = round((correctSubmissions.length / answersMeta.length) * 100);
		if (scorePercentage < 33.33) {
			resultsEl.classList.add('is-bad-result');
		} else if (scorePercentage >= 33.33 && scorePercentage < 66.66) {
			resultsEl.classList.add('is-in-between-result');
		} else if (scorePercentage >= 66.66) {
			resultsEl.classList.add('is-good-result');
		}

		showElement(resultsEl);
		dataLayerPush({
			event: 'challengeQuizComplete',
			quizName: document.querySelector('.wp-block-post-title').innerText,
			quizScore: `${correctSubmissions.length}/${answersMeta.length}`,
			timeTaken: `${getSecondsElapsed()}`,
		});
	};

	/**
	 * Handles when the stop timer button is clicked.
	 * @param {Event} event Click event on button.
	 */
	const handleStopTimer = (event) => {
		event.preventDefault();
		try {
			apiFetch({
				path: `/mavis/v1/update-challenge-action-count`,
				method: 'POST',
				data: {
					meta_key: 'mavis_challenge_stop_timer_count',
					post_id: challengeId,
				},
			});
		} catch (e) {
			console.error(e); // eslint-disable-line no-console
		}
		hideElement(stopTimerBtn, true);
		hideElement(howAreYouDoingText, false);
		clearInterval(timerInterval);
		timerTextEl.textContent = __('Timer stopped. Play at your own pace.', 'mavis');
		dataLayerPush({
			event: `challengeQuizInteraction`,
			interactionType: `stopTimer`,
			quizName: document.querySelector('.wp-block-post-title').innerText,
			challengeTimestamp: `${getSecondsElapsed()}`,
		});
	};

	/**
	 * Handles changing the time displayed by the timer.
	 */
	const handleTimer = () => {
		const minutes = parseInt(fullDurationInSeconds / 60, 10);
		const seconds = parseInt(fullDurationInSeconds % 60, 10);

		if (minutes <= 0 && seconds <= 0) {
			clearInterval(timerInterval);
			hidePlayingArea();
			showElement(seeResultsBtn, true);
			timerTextEl.textContent = __("Sorry you've ran out of time", 'mavis');
			return;
		}

		const minutesDisplay =
			minutes > 0
				? _nx(`${minutes} minute`, `${minutes} minutes`, minutes, 'number of minutes', 'mavis')
				: '';

		const secondsDisplay =
			seconds > 0
				? _nx(`${seconds} second`, `${seconds} seconds`, seconds, 'number of seconds', 'mavis')
				: '';

		timerTextEl.innerHTML = `<strong>${minutesDisplay}</strong> ${
			minutes && seconds > 0 ? 'and' : ''
		} <strong>${secondsDisplay}</strong> left`;
		fullDurationInSeconds -= 1;
	};

	/**
	 * Handles showing new elements when the quiz timer is started.
	 */
	const startAndDisplayTimer = () => {
		showElement(timerTextEl);
		showElement(buttonsWrapper);
		showElement(answersOutputWrapperEl);
		showElement(answerCountEl, false);
		showElement(howAreYouDoingText, false);
		hideElement(initTextEl);
		handleTimer();
		timerInterval = setInterval(() => handleTimer(), 1000);
	};

	/**
	 * Checks if an incorrect answer matches an already entered main answer,
	 * or a synonym of an already entered main answer.
	 */
	const checkAlreadyEnteredAnswer = (textToCheck) => {
		const lowerCaseSubmissions = arrayToLowerCase(correctSubmissions);
		if (lowerCaseSubmissions.includes(textToCheck)) {
			return true;
		}

		// Get the main answer text of a potential synonym.
		const synonymAnswerObj = answersMeta.find((answerObj) =>
			arrayToLowerCase(answerObj.synonyms).includes(textToCheck),
		);
		if (!synonymAnswerObj) {
			return false;
		}

		return lowerCaseSubmissions.includes(synonymAnswerObj.text.toLowerCase());
	};

	/**
	 * Handles the user typing in the quiz input.
	 *
	 * @param {Event} event Keydown event.
	 */
	const handleInputKeydown = (event) => {
		if (!incorrectAnswerText.classList.contains('is-hidden')) {
			hideElement(incorrectAnswerText);
			incorrectAnswerText.textContent = '';
			inputEl.classList.remove('is-incorrect');
		}

		if (event.keyCode !== 13 && !event.target.classList.contains('is-submit')) {
			return;
		}
		event.preventDefault();

		// Convert both inputted text & correct answers to lower case for comparison.
		const textToCheck = inputEl.value.toLowerCase().trim();
		const correctAnswer = correctAnswers.find(
			(answerObj) => answerObj.text.toLowerCase() === textToCheck,
		);

		if (shouldStartTimer) {
			startAndDisplayTimer();
			shouldStartTimer = false;
		}

		if (!correctAnswer) {
			inputEl.classList.add('is-incorrect');
			incorrectAnswerText.textContent = __('Wrong answer', 'mavis');

			if (checkAlreadyEnteredAnswer(textToCheck)) {
				incorrectAnswerText.textContent = __("You've already added that", 'mavis');
			}

			showElement(incorrectAnswerText);
			return;
		}

		correctSubmissions.push(correctAnswer?.parentText || correctAnswer.text);

		if (correctSubmissions.length === answersMeta.length) {
			clearInterval(timerInterval);
			try {
				apiFetch({
					path: `/mavis/v1/update-challenge-action-count`,
					method: 'POST',
					data: {
						meta_key: 'mavis_challenge_all_correct_count',
						post_id: challengeId,
					},
				});
			} catch (e) {
				console.error(e); // eslint-disable-line no-console
			}
			hidePlayingArea();
			timerTextEl.textContent = __('Well done you got them all!', 'mavis');
			showElement(seeResultsBtn, true);
			dataLayerPush({
				event: `challengeQuizInteraction`,
				interactionType: `allCorrectAnswers`,
				quizName: document.querySelector('.wp-block-post-title').innerText,
				challengeTimestamp: `${getSecondsElapsed()}`,
			});
		}

		currentScoreEl.textContent = correctSubmissions.length;
		inputEl.value = '';
		answersOutputEl.textContent = correctSubmissions.join(', ');
		apiFetch({
			path: `/mavis/v1/update-challenge-answer-count`,
			method: 'POST',
			data: {
				answer_id: correctAnswer.id,
				post_id: challengeId,
				update_total_participants: shouldUpdateTotalParticipants,
			},
		});
		shouldUpdateTotalParticipants = false;
		correctAnswers = correctAnswers.filter((answerObj) => answerObj.id !== correctAnswer.id);
	};

	/**
	 * Handle the 'Give up' button being pressed.
	 * @param {Event} event Click event on give up button.
	 */
	const handleGiveUpBtn = (event) => {
		event.preventDefault();
		dataLayerPush({
			event: `challengeQuizInteraction`,
			interactionType: `giveUp`,
			quizName: document.querySelector('.wp-block-post-title').innerText,
			challengeTimestamp: `${getSecondsElapsed()}`,
		});

		try {
			apiFetch({
				path: `/mavis/v1/update-challenge-action-count`,
				method: 'POST',
				data: {
					meta_key: 'mavis_challenge_give_up_count',
					post_id: challengeId,
				},
			});
		} catch (e) {
			console.error(e); // eslint-disable-line no-console
		}
		showResults(event);
	};

	populateAnswersArray();

	// Add event listeners.
	inputEl.addEventListener('keydown', (e) => handleInputKeydown(e));
	submitBtn.addEventListener('click', (e) => handleInputKeydown(e));
	stopTimerBtn.addEventListener('click', (e) => handleStopTimer(e));
	seeResultsBtn.addEventListener('click', (e) => showResults(e));
	giveUpBtn.addEventListener('click', (e) => handleGiveUpBtn(e));
	shareBtn.addEventListener('click', (e) => copyURLToClipboard(e));
	playAgainBtn.addEventListener('click', (e) => handlePlayAgain(e));
});
